import { type Graph } from '../models/graph';
import { type Level } from '../models/level';
import { currentLook } from './look';
import { hashString, mixSeed, mulberry32 } from './rng';
import { EMPTY_SPRITE_CATALOG, pickSprite, type SpriteCatalog } from './sprites';

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Rect {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export interface RoadSegment {
  readonly a: Point;
  readonly b: Point;
  readonly width: number;
  readonly major: boolean;
  readonly edgeId?: string;
}

export interface Plaza {
  readonly center: Point;
  readonly radius: number;
}

export interface CanalPlan {
  readonly path: readonly Point[];
  readonly width: number;
}

export interface PlacedBuilding {
  readonly spriteId: string | null;
  readonly group: string;
  readonly anchor: Point;
  readonly width: number;
  readonly height: number;
}

export interface CityPlan {
  readonly levelId: string;
  readonly scene: Rect;
  readonly roads: readonly RoadSegment[];
  readonly alleys: readonly RoadSegment[];
  readonly sideStreets: readonly RoadSegment[];
  readonly crossings: readonly Crossing[];
  readonly plazas: readonly Plaza[];
  readonly canal: CanalPlan;
  readonly buildings: readonly PlacedBuilding[];
  readonly board?: BoardFrame;
}

export interface BoardFrame {
  readonly rect: Rect;
  readonly frame: number;
  readonly lampHeight: number;
}

// Two level edges that cross where the graph has no node. One of them is drawn
// as a bridge, so the crossing does not read as a junction.
export interface Crossing {
  readonly at: Point;
  readonly over: string;
  readonly under: string;
}

export interface Landmark {
  readonly spriteId: string;
  readonly anchor: Point;
}

export interface CityPlanOptions {
  readonly scene?: Rect;
  readonly catalog?: SpriteCatalog;
  readonly sizeScale?: number;
  readonly canalPath?: readonly Point[] | null;
  readonly landmarks?: readonly Landmark[];
  readonly style?: CityStyle;
}

export interface CityStyle {
  readonly streetScale: number;
  readonly buildChance: number;
  readonly sideStreets: boolean;
  readonly roadClearance: number;
  readonly plazaAtEveryNode: boolean;
  readonly keepFacadesOffRoads: boolean;
  readonly backStreets: boolean;
  readonly frontage: boolean;
  readonly maxBuildings: number;
  // Bounding the area instead of the house count makes streets and houses end together.
  readonly cityReach: number;
}

export const CLASSIC_CITY: CityStyle = {
  streetScale: 1,
  buildChance: 0.7,
  sideStreets: false,
  roadClearance: 0.005,
  plazaAtEveryNode: false,
  keepFacadesOffRoads: false,
  backStreets: false,
  frontage: false,
  maxBuildings: 4000,
  cityReach: Number.POSITIVE_INFINITY,
};
export const DIORAMA_CITY: CityStyle = {
  streetScale: 0.58,
  buildChance: 0.9,
  sideStreets: true,
  roadClearance: 0.016,
  plazaAtEveryNode: true,
  keepFacadesOffRoads: true,
  backStreets: true,
  frontage: true,
  maxBuildings: Number.POSITIVE_INFINITY,
  cityReach: 80,
};

function defaultCityStyle(): CityStyle {
  return currentLook() === 'diorama' ? DIORAMA_CITY : CLASSIC_CITY;
}

export const LANDMARK_GROUP = 'landmarks';

const MAJOR_ROAD_WIDTH = 0.064;
const MINOR_ROAD_WIDTH = 0.042;
const ALLEY_WIDTH = 0.023;
export const CANAL_WIDTH = 0.078;
const JUNCTION_DEGREE = 3;

const GRID_CELL = 0.044;
const SIDE_STREET_LENGTH = 0.9;
const SIDE_STREET_WIDTH = 0.032;
export const NODE_REACH = 0.075;
const SIDE_STREET_SPREAD = 1.3;
const BACK_STREET_SPACING = 0.15;
const BACK_STREET_WIDTH = 0.026;
const BACK_STREET_JITTER = 0.22;
const BACK_STREET_CHANCE = 0.8;
const FRONTAGE_DEPTH = 0.065;
const COURTYARD_CHANCE = 0.35;
const SCENE_MARGIN = 0.4;
const CITY_FRINGE = 16;

export const GROUP_HEIGHT: Record<string, number> = {
  residential: 0.082,
  industrial: 0.098,
  mixed: 0.088,
  landmarks: 0.125,
};
export const DEFAULT_HEIGHT = 0.088;
export const FALLBACK_ASPECT = 0.72;

const REFERENCE_EXTENT = 0.95;
const MIN_SIZE_SCALE = 0.5;
const MAX_SIZE_SCALE = 1.4;

export function seedForLevel(levelId: string): number {
  return hashString(`graphquest:city:${levelId}`);
}

// A level whose nodes huddle in a corner is projected at a larger unit scale, so
// fixed normalized sizes would render as giant houses on giant roads. Tying the
// city's metrics to the graph's own extent keeps streets and buildings roughly
// the same size in pixels from level to level.
export function sizeScaleFor(graph: Graph): number {
  const bounds = graphBounds(graph, 0);
  const extent = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
  if (!(extent > 0)) return MIN_SIZE_SCALE;
  return Math.min(MAX_SIZE_SCALE, Math.max(MIN_SIZE_SCALE, extent / REFERENCE_EXTENT));
}

export function graphBounds(graph: Graph, pad = 0.04): Rect {
  if (graph.nodes.length === 0) return { minX: -pad, minY: -pad, maxX: 1 + pad, maxY: 1 + pad };

  const xs = graph.nodes.map((n) => n.position.x);
  const ys = graph.nodes.map((n) => n.position.y);
  return {
    minX: Math.min(...xs) - pad,
    minY: Math.min(...ys) - pad,
    maxX: Math.max(...xs) + pad,
    maxY: Math.max(...ys) + pad,
  };
}

export function expandRect(rect: Rect, margin: number): Rect {
  const width = Math.max(rect.maxX - rect.minX, 0.1);
  const height = Math.max(rect.maxY - rect.minY, 0.1);
  return {
    minX: rect.minX - width * margin,
    minY: rect.minY - height * margin,
    maxX: rect.maxX + width * margin,
    maxY: rect.maxY + height * margin,
  };
}

function intersectRects(a: Rect, b: Rect): Rect {
  return {
    minX: Math.max(a.minX, b.minX),
    minY: Math.max(a.minY, b.minY),
    maxX: Math.min(a.maxX, b.maxX),
    maxY: Math.min(a.maxY, b.maxY),
  };
}

function contains(rect: Rect, p: Point): boolean {
  return p.x >= rect.minX && p.x <= rect.maxX && p.y >= rect.minY && p.y <= rect.maxY;
}

interface CityBounds {
  readonly rect: Rect;
  fade(p: Point): number;
}

function cityBounds(graph: Graph, sizeScale: number, style: CityStyle): CityBounds {
  const bounds = graphBounds(graph);
  const cell = GRID_CELL * sizeScale;
  const reach = style.cityReach * cell;
  const rect = {
    minX: bounds.minX - reach,
    minY: bounds.minY - reach,
    maxX: bounds.maxX + reach,
    maxY: bounds.maxY + reach,
  };
  const fringe = CITY_FRINGE * cell;
  return {
    rect,
    fade: (p) =>
      Math.min(
        1,
        Math.min(p.x - rect.minX, rect.maxX - p.x, p.y - rect.minY, rect.maxY - p.y) / fringe,
      ),
  };
}

function degrees(graph: Graph): Map<string, number> {
  const counts = new Map<string, number>();
  for (const edge of graph.edges) {
    counts.set(edge.sourceId, (counts.get(edge.sourceId) ?? 0) + 1);
    counts.set(edge.targetId, (counts.get(edge.targetId) ?? 0) + 1);
  }
  return counts;
}

export function nodePositions(graph: Graph): Map<string, Point> {
  return new Map(graph.nodes.map((n) => [n.id, n.position]));
}

export function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(p.x - a.x, p.y - a.y);

  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function distanceToPath(p: Point, path: readonly Point[]): number {
  let best = Number.POSITIVE_INFINITY;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    if (!a || !b) continue;
    best = Math.min(best, distanceToSegment(p, a, b));
  }
  return best;
}

export function segmentsCross(a1: Point, a2: Point, b1: Point, b2: Point): boolean {
  const d = (p: Point, q: Point, r: Point) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const d1 = d(b1, b2, a1);
  const d2 = d(b1, b2, a2);
  const d3 = d(a1, a2, b1);
  const d4 = d(a1, a2, b2);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

export function planRoads(graph: Graph, sizeScale: number, streetScale: number): RoadSegment[] {
  const positions = nodePositions(graph);
  const degree = degrees(graph);
  const roads: RoadSegment[] = [];

  for (const edge of graph.edges) {
    const a = positions.get(edge.sourceId);
    const b = positions.get(edge.targetId);
    if (!a || !b) continue;

    const major =
      Math.max(degree.get(edge.sourceId) ?? 0, degree.get(edge.targetId) ?? 0) >= JUNCTION_DEGREE;
    const width = (major ? MAJOR_ROAD_WIDTH : MINOR_ROAD_WIDTH) * sizeScale * streetScale;
    roads.push({ a, b, width, major, edgeId: edge.id });
  }
  return roads;
}

export function planPlazas(
  graph: Graph,
  sizeScale: number,
  style: Pick<CityStyle, 'streetScale' | 'plazaAtEveryNode'>,
): Plaza[] {
  const degree = degrees(graph);
  const plazas: Plaza[] = [];
  const streetScale = style.streetScale;

  for (const node of graph.nodes) {
    const deg = degree.get(node.id) ?? 0;
    if (deg < JUNCTION_DEGREE && !style.plazaAtEveryNode) continue;
    const radius = (0.034 + 0.005 * Math.min(deg, 6)) * sizeScale * streetScale;
    plazas.push({ center: node.position, radius });
  }
  return plazas;
}

export function angularGap(a: number, b: number): number {
  return Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
}

function freeAngle(rng: () => number, taken: readonly number[], minGap: number): number {
  let candidate = rng() * Math.PI * 2;
  for (let attempt = 0; attempt < 10; attempt++) {
    candidate = rng() * Math.PI * 2;
    if (taken.every((angle) => angularGap(candidate, angle) > minGap)) return candidate;
  }
  return candidate;
}

function tipIsClear(
  tip: Point,
  graph: Graph,
  roads: readonly RoadSegment[],
  sizeScale: number,
): boolean {
  for (const node of graph.nodes) {
    const reach = NODE_REACH * sizeScale;
    if (Math.hypot(tip.x - node.position.x, tip.y - node.position.y) < reach) return false;
  }
  for (const road of roads) {
    if (distanceToSegment(tip, road.a, road.b) < road.width / 2 + 0.02 * sizeScale) return false;
  }
  return true;
}

function crossesRoad(a: Point, b: Point, roads: readonly RoadSegment[]): boolean {
  return roads.some((road) => segmentsCross(a, b, road.a, road.b));
}

function planAlleys(
  graph: Graph,
  roads: readonly RoadSegment[],
  seed: number,
  sizeScale: number,
  streetScale: number,
): RoadSegment[] {
  const positions = nodePositions(graph);
  const alleys: RoadSegment[] = [];
  const alleyWidth = ALLEY_WIDTH * sizeScale * streetScale;

  for (const node of graph.nodes) {
    const rng = mulberry32(mixSeed(seed, hashString(node.id), 0x51ed));
    const taken: number[] = [];
    for (const edge of graph.edges) {
      const other =
        edge.sourceId === node.id
          ? positions.get(edge.targetId)
          : edge.targetId === node.id
            ? positions.get(edge.sourceId)
            : undefined;
      if (other) taken.push(Math.atan2(other.y - node.position.y, other.x - node.position.x));
    }

    const count = 1 + Math.floor(rng() * 2);
    for (let i = 0; i < count; i++) {
      const angle = freeAngle(rng, taken, 0.55);
      const length = (0.055 + rng() * 0.07) * sizeScale;
      const tip = {
        x: node.position.x + Math.cos(angle) * length,
        y: node.position.y + Math.sin(angle) * length,
      };
      if (!tipIsClear(tip, graph, roads, sizeScale)) continue;
      if (crossesRoad(node.position, tip, roads)) continue;

      taken.push(angle);
      alleys.push({ a: node.position, b: tip, width: alleyWidth, major: false });
    }
  }

  for (const edge of graph.edges) {
    const a = positions.get(edge.sourceId);
    const b = positions.get(edge.targetId);
    if (!a || !b) continue;

    const rng = mulberry32(mixSeed(seed, hashString(edge.id), 0x2f1b));
    if (rng() < 0.4) continue;

    const t = 0.32 + rng() * 0.36;
    const root = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    const side = rng() < 0.5 ? 1 : -1;
    const angle = Math.atan2(b.y - a.y, b.x - a.x) + (side * Math.PI) / 2 + (rng() - 0.5) * 0.45;
    const length = (0.048 + rng() * 0.065) * sizeScale;
    const tip = { x: root.x + Math.cos(angle) * length, y: root.y + Math.sin(angle) * length };

    if (!tipIsClear(tip, graph, roads, sizeScale)) continue;
    if (crossesRoad(root, tip, roads)) continue;

    alleys.push({ a: root, b: tip, width: alleyWidth, major: false });
  }

  return alleys;
}

export function crossesAny(a: Point, b: Point, segments: readonly RoadSegment[]): boolean {
  return segments.some((s) => segmentsCross(a, b, s.a, s.b));
}

export function passesNode(
  a: Point,
  b: Point,
  graph: Graph,
  origin: string | null,
  reach: number,
): boolean {
  return graph.nodes.some(
    (node) => node.id !== origin && distanceToSegment(node.position, a, b) < reach,
  );
}

// Streets that carry the level's network on into the city. Their length is fixed
// in map units rather than clipped to the scene, so a building's cell is blocked
// or free the same way whatever viewport renders it.
function planSideStreets(
  graph: Graph,
  roads: readonly RoadSegment[],
  seed: number,
  sizeScale: number,
  streetScale: number,
): RoadSegment[] {
  if (graph.nodes.length === 0) return [];
  const positions = nodePositions(graph);
  const cx = graph.nodes.reduce((sum, n) => sum + n.position.x, 0) / graph.nodes.length;
  const cy = graph.nodes.reduce((sum, n) => sum + n.position.y, 0) / graph.nodes.length;
  const width = SIDE_STREET_WIDTH * sizeScale * streetScale;
  const length = SIDE_STREET_LENGTH * sizeScale;
  const reach = NODE_REACH * sizeScale;
  const streets: RoadSegment[] = [];

  for (const node of graph.nodes) {
    const rng = mulberry32(mixSeed(seed, hashString(node.id), 0x5e1d));
    const taken: number[] = [];
    for (const edge of graph.edges) {
      const otherId =
        edge.sourceId === node.id
          ? edge.targetId
          : edge.targetId === node.id
            ? edge.sourceId
            : null;
      const other = otherId ? positions.get(otherId) : undefined;
      if (other) taken.push(Math.atan2(other.y - node.position.y, other.x - node.position.x));
    }

    const outward = Math.atan2(node.position.y - cy, node.position.x - cx);
    const count = rng() < 0.6 ? 1 : 2;
    for (let i = 0; i < count; i++) {
      const angle = outward + (rng() - 0.5) * SIDE_STREET_SPREAD;
      if (taken.some((t) => angularGap(t, angle) < 0.6)) continue;
      const tip = {
        x: node.position.x + Math.cos(angle) * length,
        y: node.position.y + Math.sin(angle) * length,
      };
      if (crossesAny(node.position, tip, roads) || crossesAny(node.position, tip, streets)) {
        continue;
      }
      if (passesNode(node.position, tip, graph, node.id, reach)) continue;
      taken.push(angle);
      streets.push({ a: node.position, b: tip, width, major: false });

      for (let k = 0; k < 2; k++) {
        if (rng() < 0.35) continue;
        const t = 0.12 + rng() * 0.3;
        const root = {
          x: node.position.x + (tip.x - node.position.x) * t,
          y: node.position.y + (tip.y - node.position.y) * t,
        };
        const side = rng() < 0.5 ? 1 : -1;
        const turn = angle + (side * Math.PI) / 2 + (rng() - 0.5) * 0.3;
        const span = (0.12 + rng() * 0.22) * sizeScale;
        const end = { x: root.x + Math.cos(turn) * span, y: root.y + Math.sin(turn) * span };
        if (crossesAny(root, end, roads) || crossesAny(root, end, streets)) continue;
        if (passesNode(root, end, graph, null, reach)) continue;
        streets.push({ a: root, b: end, width: width * 0.8, major: false });
      }
    }
  }
  return streets;
}

// Buckets segments by area so a building only tests the lanes around it; the
// backdrop network runs to thousands of segments on the city map.
class LaneIndex {
  private readonly buckets = new Map<string, RoadSegment[]>();
  readonly widest: number;

  constructor(
    readonly segments: readonly RoadSegment[],
    private readonly size: number,
  ) {
    this.widest = segments.reduce((max, s) => Math.max(max, s.width), 0);
    for (const segment of segments) {
      const [x0, x1] = [segment.a.x, segment.b.x].sort((p, q) => p - q) as [number, number];
      const [y0, y1] = [segment.a.y, segment.b.y].sort((p, q) => p - q) as [number, number];
      for (let bx = Math.floor(x0 / size); bx <= Math.floor(x1 / size); bx++) {
        for (let by = Math.floor(y0 / size); by <= Math.floor(y1 / size); by++) {
          const key = `${bx}:${by}`;
          const bucket = this.buckets.get(key);
          if (bucket) bucket.push(segment);
          else this.buckets.set(key, [segment]);
        }
      }
    }
  }

  near(p: Point, radius: number): RoadSegment[] {
    const found = new Set<RoadSegment>();
    for (
      let bx = Math.floor((p.x - radius) / this.size);
      bx <= Math.floor((p.x + radius) / this.size);
      bx++
    ) {
      for (
        let by = Math.floor((p.y - radius) / this.size);
        by <= Math.floor((p.y + radius) / this.size);
        by++
      ) {
        for (const segment of this.buckets.get(`${bx}:${by}`) ?? []) found.add(segment);
      }
    }
    return [...found];
  }
}

// A jittered, slightly turned lattice of backdrop streets. Every vertex and
// every link is seeded by its absolute lattice index, so the network is the
// same whatever part of it a viewport shows.
function planBackStreets(
  graph: Graph,
  roads: readonly RoadSegment[],
  scene: Rect,
  city: CityBounds,
  seed: number,
  sizeScale: number,
  streetScale: number,
): RoadSegment[] {
  const area = intersectRects(scene, city.rect);
  const spacing = BACK_STREET_SPACING * sizeScale;
  const width = BACK_STREET_WIDTH * sizeScale * streetScale;
  const reach = NODE_REACH * sizeScale;
  const turn = (mulberry32(mixSeed(seed, 0x7a11))() - 0.5) * 0.7;
  const cos = Math.cos(turn);
  const sin = Math.sin(turn);

  const vertex = (i: number, j: number): Point => {
    const rng = mulberry32(mixSeed(seed, i, j, 0xb5c7));
    const u = (i + (rng() - 0.5) * 2 * BACK_STREET_JITTER) * spacing;
    const v = (j + (rng() - 0.5) * 2 * BACK_STREET_JITTER) * spacing;
    return { x: u * cos - v * sin, y: u * sin + v * cos };
  };

  const corners = [
    { x: area.minX, y: area.minY },
    { x: area.maxX, y: area.minY },
    { x: area.minX, y: area.maxY },
    { x: area.maxX, y: area.maxY },
  ].map((c) => ({ u: c.x * cos + c.y * sin, v: -c.x * sin + c.y * cos }));
  const iMin = Math.floor(Math.min(...corners.map((c) => c.u)) / spacing) - 1;
  const iMax = Math.ceil(Math.max(...corners.map((c) => c.u)) / spacing) + 1;
  const jMin = Math.floor(Math.min(...corners.map((c) => c.v)) / spacing) - 1;
  const jMax = Math.ceil(Math.max(...corners.map((c) => c.v)) / spacing) + 1;

  const clearOfRoads = (a: Point, b: Point): boolean => {
    if (crossesAny(a, b, roads)) return false;
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    return roads.every((road) =>
      [a, mid, b].every((p) => distanceToSegment(p, road.a, road.b) > road.width / 2 + width),
    );
  };

  const streets: RoadSegment[] = [];
  for (let i = iMin; i <= iMax; i++) {
    for (let j = jMin; j <= jMax; j++) {
      const a = vertex(i, j);
      for (const [di, dj, salt] of [
        [1, 0, 1],
        [0, 1, 2],
      ] as const) {
        const b = vertex(i + di, j + dj);
        if (!contains(city.rect, a) || !contains(city.rect, b)) continue;
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const chance = BACK_STREET_CHANCE * city.fade(mid) ** 2;
        if (mulberry32(mixSeed(seed, i, j, salt, 0x3e9f))() > chance) continue;
        if (!clearOfRoads(a, b)) continue;
        if (passesNode(a, b, graph, null, reach)) continue;
        streets.push({ a, b, width, major: false });
      }
    }
  }
  return streets;
}

function sharesEnd(r: RoadSegment, s: RoadSegment): boolean {
  const same = (p: Point, q: Point) => p.x === q.x && p.y === q.y;
  return same(r.a, s.a) || same(r.a, s.b) || same(r.b, s.a) || same(r.b, s.b);
}

export function planCrossings(roads: readonly RoadSegment[]): Crossing[] {
  const crossings: Crossing[] = [];
  for (let i = 0; i < roads.length; i++) {
    for (let j = i + 1; j < roads.length; j++) {
      const r = roads[i]!;
      const s = roads[j]!;
      if (!r.edgeId || !s.edgeId || sharesEnd(r, s)) continue;
      if (!segmentsCross(r.a, r.b, s.a, s.b)) continue;
      const denominator = (r.b.x - r.a.x) * (s.b.y - s.a.y) - (r.b.y - r.a.y) * (s.b.x - s.a.x);
      const t =
        ((s.a.x - r.a.x) * (s.b.y - s.a.y) - (s.a.y - r.a.y) * (s.b.x - s.a.x)) / denominator;
      const at = { x: r.a.x + (r.b.x - r.a.x) * t, y: r.a.y + (r.b.y - r.a.y) * t };
      const sOver = s.major || !r.major;
      crossings.push({ at, over: sOver ? s.edgeId : r.edgeId, under: sOver ? r.edgeId : s.edgeId });
    }
  }
  return crossings;
}

function planCanal(graph: Graph, scene: Rect, seed: number, sizeScale: number): CanalPlan {
  const sceneWidth = scene.maxX - scene.minX;
  const sceneHeight = scene.maxY - scene.minY;
  const horizontal = sceneWidth >= sceneHeight;
  const rng = mulberry32(mixSeed(seed, 0x9d2c, horizontal ? 1 : 0));

  const crossMin = horizontal ? scene.minY : scene.minX;
  const crossMax = horizontal ? scene.maxY : scene.maxX;
  const alongMin = horizontal ? scene.minX : scene.minY;
  const alongMax = horizontal ? scene.maxX : scene.maxY;

  let bestBase = (crossMin + crossMax) / 2;
  let bestScore = -1;
  for (let i = 1; i <= 7; i++) {
    const base = crossMin + ((crossMax - crossMin) * i) / 8;
    let score = Number.POSITIVE_INFINITY;
    for (const node of graph.nodes) {
      const cross = horizontal ? node.position.y : node.position.x;
      score = Math.min(score, Math.abs(cross - base));
    }
    if (score > bestScore) {
      bestScore = score;
      bestBase = base;
    }
  }

  const steps = 6;
  const wobble = (crossMax - crossMin) * 0.045;
  const path: Point[] = [];
  for (let i = 0; i <= steps; i++) {
    const along = alongMin + ((alongMax - alongMin) * i) / steps;
    const cross = bestBase + Math.sin(i * 1.7 + rng() * 0.4) * wobble;
    path.push(horizontal ? { x: along, y: cross } : { x: cross, y: along });
  }

  return { path, width: CANAL_WIDTH * sizeScale };
}

const INHERITED_CANAL_STEP = 0.16;
const INHERITED_CANAL_WOBBLE = 0.012;

export function inheritedCanal(
  course: readonly Point[],
  seed: number,
  sizeScale: number,
): CanalPlan {
  const rng = mulberry32(mixSeed(seed, 0x51c4));
  const path: Point[] = [];
  let phase = rng() * Math.PI * 2;

  for (let i = 0; i < course.length - 1; i++) {
    const a = course[i]!;
    const b = course[i + 1]!;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    const pieces = Math.max(1, Math.round(length / (INHERITED_CANAL_STEP * sizeScale)));
    const nx = length > 0 ? -(b.y - a.y) / length : 0;
    const ny = length > 0 ? (b.x - a.x) / length : 0;

    for (let k = 0; k < pieces; k++) {
      const t = k / pieces;
      const offset = k === 0 ? 0 : Math.sin(phase + k * 1.9) * INHERITED_CANAL_WOBBLE * sizeScale;
      path.push({ x: a.x + (b.x - a.x) * t + nx * offset, y: a.y + (b.y - a.y) * t + ny * offset });
    }
    phase += pieces * 1.9;
  }
  const last = course[course.length - 1];
  if (last) path.push(last);

  return { path, width: CANAL_WIDTH * sizeScale };
}

function buildingShape(
  catalog: SpriteCatalog,
  groupRoll: number,
  spriteRoll: number,
  sizeRoll: number,
  sizeScale: number,
): Omit<PlacedBuilding, 'anchor'> {
  const sprite = pickSprite(catalog, groupRoll, spriteRoll);
  const group = sprite?.group ?? 'block';
  const height = (GROUP_HEIGHT[group] ?? DEFAULT_HEIGHT) * (0.84 + sizeRoll * 0.34) * sizeScale;
  const aspect = sprite ? sprite.width / sprite.height : FALLBACK_ASPECT;

  return { spriteId: sprite?.id ?? null, group, width: height * aspect, height };
}

function isBlocked(
  anchor: Point,
  halfFootprint: number,
  roads: readonly RoadSegment[],
  lanes: LaneIndex,
  plazas: readonly Plaza[],
  canal: CanalPlan,
  sizeScale: number,
  roadClearance: number,
): boolean {
  for (const road of roads) {
    const clearance = road.width / 2 + halfFootprint * 0.5 + roadClearance * sizeScale;
    if (distanceToSegment(anchor, road.a, road.b) < clearance) return true;
  }
  for (const alley of lanes.near(anchor, lanes.widest / 2 + halfFootprint * 0.35)) {
    if (distanceToSegment(anchor, alley.a, alley.b) < alley.width / 2 + halfFootprint * 0.35) {
      return true;
    }
  }
  for (const plaza of plazas) {
    const clearance = plaza.radius + 0.01 * sizeScale;
    if (Math.hypot(anchor.x - plaza.center.x, anchor.y - plaza.center.y) < clearance) return true;
  }
  return (
    distanceToPath(anchor, canal.path) < canal.width / 2 + halfFootprint * 0.6 + 0.008 * sizeScale
  );
}

// Sprites stand on their anchor and rise towards the top of the map, so a house
// just below a street would paint its roof across it.
function facadeCoversRoad(
  anchor: Point,
  shape: Omit<PlacedBuilding, 'anchor'>,
  roads: readonly RoadSegment[],
  plazas: readonly Plaza[],
): boolean {
  for (const rise of [0.35, 0.7]) {
    const point = { x: anchor.x, y: anchor.y - shape.height * rise };
    const reach = shape.width * 0.32;
    for (const road of roads) {
      if (distanceToSegment(point, road.a, road.b) < road.width / 2 + reach) return true;
    }
    for (const plaza of plazas) {
      if (Math.hypot(point.x - plaza.center.x, point.y - plaza.center.y) < plaza.radius + reach) {
        return true;
      }
    }
  }
  return false;
}

function placeLandmarks(
  landmarks: readonly Landmark[],
  catalog: SpriteCatalog,
  roads: readonly RoadSegment[],
  lanes: LaneIndex,
  plazas: readonly Plaza[],
  canal: CanalPlan,
  sizeScale: number,
  roadClearance: number,
): PlacedBuilding[] {
  const placed: PlacedBuilding[] = [];
  for (const landmark of landmarks) {
    const sprite = catalog.sprites.find((s) => s.id === landmark.spriteId);
    if (!sprite) continue;
    const height = (GROUP_HEIGHT[sprite.group] ?? DEFAULT_HEIGHT) * sizeScale;
    const width = height * (sprite.width / sprite.height);
    if (
      isBlocked(landmark.anchor, width / 2, roads, lanes, plazas, canal, sizeScale, roadClearance)
    ) {
      continue;
    }
    placed.push({
      spriteId: sprite.id,
      group: sprite.group,
      anchor: landmark.anchor,
      width,
      height,
    });
  }
  return placed;
}

function overlapsReserved(
  anchor: Point,
  shape: Omit<PlacedBuilding, 'anchor'>,
  reserved: readonly PlacedBuilding[],
): boolean {
  return reserved.some(
    (r) =>
      Math.abs(anchor.x - r.anchor.x) < (shape.width + r.width) * 0.4 &&
      Math.abs(anchor.y - r.anchor.y) < (shape.height + r.height) * 0.3,
  );
}

function planBuildings(
  scene: Rect,
  city: CityBounds,
  seed: number,
  catalog: SpriteCatalog,
  roads: readonly RoadSegment[],
  lanes: LaneIndex,
  plazas: readonly Plaza[],
  canal: CanalPlan,
  sizeScale: number,
  style: CityStyle,
  reserved: readonly PlacedBuilding[] = [],
): PlacedBuilding[] {
  const buildings: PlacedBuilding[] = [...reserved];
  const cell = GRID_CELL * sizeScale;
  const area = intersectRects(scene, city.rect);

  const firstX = Math.floor(area.minX / cell);
  const lastX = Math.ceil(area.maxX / cell);
  const firstY = Math.floor(area.minY / cell);
  const lastY = Math.ceil(area.maxY / cell);

  const centerX = (area.minX + area.maxX) / 2 / cell;
  const centerY = (area.minY + area.maxY) / 2 / cell;
  const cells: { ix: number; iy: number }[] = [];
  for (let iy = firstY; iy <= lastY; iy++) {
    for (let ix = firstX; ix <= lastX; ix++) cells.push({ ix, iy });
  }
  if (Number.isFinite(style.maxBuildings)) {
    cells.sort(
      (a, b) =>
        (a.ix - centerX) ** 2 +
        (a.iy - centerY) ** 2 -
        ((b.ix - centerX) ** 2 + (b.iy - centerY) ** 2),
    );
  }

  for (const { ix, iy } of cells) {
    if (buildings.length >= style.maxBuildings) break;

    const rng = mulberry32(mixSeed(seed, ix, iy));
    const middle = { x: (ix + 0.5) * cell, y: (iy + 0.5) * cell };
    if (rng() > style.buildChance * city.fade(middle)) continue;

    const anchor = {
      x: (ix + 0.14 + rng() * 0.72) * cell,
      y: (iy + 0.14 + rng() * 0.72) * cell,
    };
    if (!contains(area, anchor)) continue;

    const shape = buildingShape(catalog, rng(), rng(), rng(), sizeScale);
    if (
      isBlocked(
        anchor,
        shape.width / 2,
        roads,
        lanes,
        plazas,
        canal,
        sizeScale,
        style.roadClearance,
      )
    ) {
      continue;
    }
    if (overlapsReserved(anchor, shape, reserved)) continue;
    if (style.keepFacadesOffRoads && facadeCoversRoad(anchor, shape, roads, plazas)) continue;
    if (style.frontage && rng() < COURTYARD_CHANCE) {
      const depth = FRONTAGE_DEPTH * sizeScale;
      const onStreet =
        roads.some((r) => distanceToSegment(anchor, r.a, r.b) < r.width / 2 + depth) ||
        lanes
          .near(anchor, depth + lanes.widest)
          .some((l) => distanceToSegment(anchor, l.a, l.b) < l.width / 2 + depth);
      if (!onStreet) continue;
    }

    buildings.push({ ...shape, anchor });
  }

  buildings.sort((a, b) => a.anchor.y - b.anchor.y || a.anchor.x - b.anchor.x);
  return buildings;
}

export function planCity(
  level: Pick<Level, 'id' | 'graph'>,
  options: CityPlanOptions = {},
): CityPlan {
  const seed = seedForLevel(level.id);
  const scene = options.scene ?? expandRect(graphBounds(level.graph), SCENE_MARGIN);
  const catalog = options.catalog ?? EMPTY_SPRITE_CATALOG;

  const sizeScale = options.sizeScale ?? sizeScaleFor(level.graph);
  const style = options.style ?? defaultCityStyle();
  const city = cityBounds(level.graph, sizeScale, style);

  const roads = planRoads(level.graph, sizeScale, style.streetScale);
  const alleys = planAlleys(level.graph, roads, seed, sizeScale, style.streetScale);
  const sideStreets = [
    ...(style.sideStreets
      ? planSideStreets(level.graph, roads, seed, sizeScale, style.streetScale)
      : []),
    ...(style.backStreets
      ? planBackStreets(level.graph, roads, scene, city, seed, sizeScale, style.streetScale)
      : []),
  ];
  const lanes = new LaneIndex([...alleys, ...sideStreets], BACK_STREET_SPACING * sizeScale);
  const plazas = planPlazas(level.graph, sizeScale, style);
  const canal =
    options.canalPath === undefined
      ? planCanal(level.graph, scene, seed, sizeScale)
      : options.canalPath === null
        ? { path: [], width: CANAL_WIDTH * sizeScale }
        : inheritedCanal(options.canalPath, seed, sizeScale);
  const inherited = options.landmarks;
  const fixed = inherited
    ? placeLandmarks(
        inherited,
        catalog,
        roads,
        lanes,
        plazas,
        canal,
        sizeScale,
        style.roadClearance,
      )
    : [];
  const buildings = planBuildings(
    scene,
    city,
    seed,
    catalog,
    roads,
    lanes,
    plazas,
    canal,
    sizeScale,
    style,
    fixed,
  );

  return {
    levelId: level.id,
    scene,
    roads,
    alleys,
    sideStreets,
    crossings: planCrossings(roads),
    plazas,
    canal,
    buildings,
  };
}
